import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { useGetAccountSettings } from "@/tanstack-hooks";
import { PlatformUnits } from "@/generated-api/models/PlatformUnits";

export const MIN_DISTANCE_KM = 0;
export const MAX_DISTANCE_KM = 1000;

const kmToMiles = (km: number | undefined) => {
  if (!km) {
    return km;
  }
  if (km === MAX_DISTANCE_KM) {
    return 620;
  }
  return Math.round(km * 0.621371);
};

const milesToKm = (miles: number) => {
  return Math.round(miles / 0.621371);
};

export default function DistanceSelector({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (value: number | null) => void;
}) {
  const accountSettingsQuery = useGetAccountSettings();
  const units: PlatformUnits | undefined =
    accountSettingsQuery.data?.preferred_units;

  const isMetric = units !== PlatformUnits.US;

  const handleDistanceChange = (sliderValue: number[]) => {
    onChange(sliderValue[0]);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue)) {
      onChange(null);
      return;
    }
    if (isMetric) {
      if (newValue > MAX_DISTANCE_KM) {
        onChange(MAX_DISTANCE_KM);
      } else {
        onChange(newValue);
      }
    } else {
      if (newValue > kmToMiles(MAX_DISTANCE_KM)!) {
        onChange(MAX_DISTANCE_KM);
      } else {
        onChange(milesToKm(newValue));
      }
    }
  };

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue) || newValue < MIN_DISTANCE_KM) {
      onChange(MIN_DISTANCE_KM);
      return;
    } else if (newValue > MAX_DISTANCE_KM) {
      onChange(MAX_DISTANCE_KM);
      return;
    }
  };

  return (
    <div className="flex items-center space-x-2 w-full max-w-2xl">
      <Slider
        value={[value ?? MIN_DISTANCE_KM]}
        onValueChange={handleDistanceChange}
        max={MAX_DISTANCE_KM}
        min={MIN_DISTANCE_KM}
        step={5}
        className="flex-grow hover:cursor-pointer"
      />
      {isMetric ? (
        <div className="flex items-center space-x-1 flex-shrink-0">
          <Input
            type="number"
            inputMode="numeric"
            value={value ?? ""}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-14 px-0 text-center text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-nowrap text-gray-400 w-fit">
            {value === MAX_DISTANCE_KM ? "+ km" : "km"}
          </span>
        </div>
      ) : (
        <div className="flex items-center space-x-1 flex-shrink-0">
          <Input
            type="number"
            inputMode="numeric"
            value={kmToMiles(value) ?? ""}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-14 px-0 text-center text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-nowrap text-gray-400 w-fit">
            {value === MAX_DISTANCE_KM ? "+ mi" : "mi"}
          </span>
        </div>
      )}
    </div>
  );
}
